import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { api, supabase, getSignedUrl } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { SignedImage } from '../../hooks/useSignedUrl';
import {
  LoanOfficer,
  ProgressWithStep,
  LOProgressSummary,
  CustomTask,
  StepCategoryRecord,
  StepStatus,
  PriorityLevel,
  OnboardingStep,
  statusColors,
  statusLabels,
  priorityColors,
  activityTypeCopy,
  a2pStatusColors,
  a2pStatusLabels,
  A2PRegistration,
  A2PBusinessRegistration,
  ActivityLog,
  ActivityType,
  StateSponsorship,
  sponsorshipStatusLabels,
  sponsorshipStatusColors,
  getOnboardingStatus,
  formatRelativeDate,
  formatLocalDate,
  formatPhone,
  STATE_ABBREVIATIONS,
} from '../../types';
import {
  ArrowLeftIcon,
  CheckIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  PlayIcon,
  PlusIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  FolderIcon,
  PhotoIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  ArrowPathIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  MapPinIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import LOComments from '../../components/admin/LOComments';
import HandoffButton from '../../components/admin/HandoffButton';
import Toast, { ToastType } from '../../components/ui/Toast';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const activityAccent: Record<ActivityType, string> = {
  task: 'bg-[#eef4ff] text-lumin-primary',
  form: 'bg-[#e0f2ff] text-[#016185]',
  document_upload: 'bg-[#e8fdf4] text-emerald-600',
  training: 'bg-[#f4e8ff] text-purple-600',
  meeting: 'bg-[#e0f0ff] text-sky-700',
  call: 'bg-[#fff2e1] text-amber-600',
  payment: 'bg-[#fff4e5] text-amber-700',
  signature: 'bg-[#ffe9f0] text-pink-600',
  video: 'bg-[#e7ecff] text-indigo-600',
  custom: 'bg-[#f2f4f7] text-slate-500',
};

// Helper to replace template variables in notes/URLs
const replaceTemplateVars = (text: string | null | undefined, locationId: string | null | undefined): string => {
  if (!text) return '';
  return text.replace(/\{\{location_id\}\}/g, locationId || '');
};

const LoanOfficerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loanOfficer: currentAdmin, isVA, isAdmin, startImpersonation } = useAuth();
  const navigation = getAdminNavigation(isVA);
  const [loanOfficer, setLoanOfficer] = useState<LoanOfficer | null>(null);
  const [progress, setProgress] = useState<ProgressWithStep[]>([]);
  const [summary, setSummary] = useState<LOProgressSummary | null>(null);
  const [tasks, setTasks] = useState<CustomTask[]>([]);
  const [categories, setCategories] = useState<StepCategoryRecord[]>([]);
  const [a2pRegistration, setA2PRegistration] = useState<A2PRegistration | null>(null);
  const [a2pBusinessRegistration, setA2PBusinessRegistration] = useState<A2PBusinessRegistration | null>(null);
  const [sponsorships, setSponsorships] = useState<StateSponsorship[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityLog[]>([]);
  const [linkedStateLicenses, setLinkedStateLicenses] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStepId, setUpdatingStepId] = useState<string | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [targetAudienceFilter, setTargetAudienceFilter] = useState<'all' | 'admin' | 'loan_officer' | 'va'>('all');
  const [stepSection, setStepSection] = useState<'onboarding' | 'hands_on'>('onboarding');
  const [activeTab, setActiveTab] = useState<'overview' | 'steps' | 'tasks' | 'a2p' | 'sponsorship' | 'activity' | 'files' | 'notes'>('overview');

  // Task assignment modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium' as PriorityLevel,
    due_date: '',
  });

  // Step assignment modal state
  const [showStepModal, setShowStepModal] = useState(false);
  const [availableSteps, setAvailableSteps] = useState<OnboardingStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<string>('');

  // Email signature upload state
  const [uploadingSignature, setUploadingSignature] = useState(false);

  // Headshot upload state
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const headshotInputRef = useRef<HTMLInputElement>(null);

  // IRS paperwork upload state
  const [uploadingIrs, setUploadingIrs] = useState(false);
  const irsInputRef = useRef<HTMLInputElement>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // A2P SOP modal state
  const [showA2PSOPModal, setShowA2PSOPModal] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  // Step detail modal state
  const [selectedStep, setSelectedStep] = useState<ProgressWithStep | null>(null);

  // Files tab state
  const [fileUrls, setFileUrls] = useState<{
    w9Url?: string | null;
    headshotUrl?: string | null;
    signatureUrl?: string | null;
    irsUrl?: string | null;
  }>({});
  const [loadingFiles, setLoadingFiles] = useState(false);

  useEffect(() => {
    if (!id) return;

    loadData();

    const channel = supabase
      .channel(`admin-lo-progress-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loan_officer_progress',
          filter: `loan_officer_id=eq.${id}`,
        },
        () => loadData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const { data: loData, error: loError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (loError) throw loError;
      if (!loData) throw new Error('Loan officer not found.');

      const [progressData, summaryData, taskData, logData, categoryData] = await Promise.all([
        api.getLOProgress(id),
        api.getProgressSummary(id),
        api.getCustomTasks(id),
        api.getActivityLog(id, 12).catch(() => []),
        api.getStepCategories(),
      ]);

      let phoneRegistration: A2PRegistration | null = null;
      try {
        phoneRegistration = await api.getA2PRegistration(id);
      } catch (phoneError) {
        // A2P data unavailable
      }

      let businessRegistration: A2PBusinessRegistration | null = null;
      try {
        businessRegistration = await api.getA2PBusinessRegistration(id);
      } catch (bizError) {
        // A2P data unavailable
      }

      let sponsorshipData: StateSponsorship[] = [];
      try {
        sponsorshipData = await api.getSponsorships(id);
      } catch (sponsorError) {
        // Sponsorship data unavailable
      }

      const { data: prospectData } = await supabase
        .from('prospects')
        .select('state_licenses')
        .eq('loan_officer_id', id)
        .maybeSingle();
      setLinkedStateLicenses(prospectData?.state_licenses ?? null);

      setLoanOfficer({ ...loData, id: String(loData.id) });
      setProgress(progressData as ProgressWithStep[]);
      setSummary(summaryData || null);
      setTasks(taskData);
      setCategories(categoryData);
      setActivityLog((logData as ActivityLog[]) || []);
      setSponsorships(sponsorshipData);
      setA2PRegistration(phoneRegistration);
      setA2PBusinessRegistration(businessRegistration);
    } catch (err: any) {
      // Error handled
      setError(err.message || 'Unable to load loan officer record.');
    } finally {
      setLoading(false);
    }
  };

  // Load file URLs when Files tab is selected
  const loadFileUrls = async () => {
    if (!id || !loanOfficer) return;

    setLoadingFiles(true);
    try {
      const urls: typeof fileUrls = {};

      // Get W9 URL
      const w9 = await api.getW9Submission(id).catch(() => null);
      if (w9?.pdf_url) {
        urls.w9Url = await api.getSignedUrl('w9-forms', w9.pdf_url);
      }

      // Get headshot URL
      if (loanOfficer.headshot) {
        urls.headshotUrl = await api.getSignedUrl('onboarding-documents', loanOfficer.headshot);
      }

      // Get email signature URL
      if (loanOfficer.email_signature_url) {
        urls.signatureUrl = await api.getSignedUrl('email-signatures', loanOfficer.email_signature_url);
      }

      // Get IRS paperwork URL from A2P registration
      if (a2pBusinessRegistration?.irs_paperwork) {
        urls.irsUrl = await api.getSignedUrl('onboarding-documents', a2pBusinessRegistration.irs_paperwork);
      }

      setFileUrls(urls);
    } catch (error) {
      console.error('Error loading file URLs:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Load file URLs when Files or A2P tab is selected, or when A2P SOP modal is opened
  // Also reload if a2pBusinessRegistration becomes available and we don't have irsUrl yet
  useEffect(() => {
    const needsIrsUrl = a2pBusinessRegistration?.irs_paperwork && !fileUrls.irsUrl;
    const shouldLoad = (activeTab === 'files' || activeTab === 'a2p' || showA2PSOPModal) && loanOfficer && (Object.keys(fileUrls).length === 0 || needsIrsUrl);
    if (shouldLoad) {
      loadFileUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loanOfficer, a2pBusinessRegistration, showA2PSOPModal]);

  const filteredProgress = useMemo(() => {
    // Filter by section first
    let filtered = progress.filter((item) => item.step.section === stepSection);

    // VAs cannot see admin-only tasks
    if (isVA) {
      filtered = filtered.filter((item) => item.step.target_audience !== 'admin');
    }

    if (targetAudienceFilter === 'all') return filtered;
    return filtered.filter((item) => item.step.target_audience === targetAudienceFilter);
  }, [progress, targetAudienceFilter, isVA, stepSection]);

  // Check if all dependencies for a step are completed
  const areDependenciesMet = (item: ProgressWithStep): boolean => {
    const deps = item.step.dependencies;
    if (!deps || deps.length === 0) return true;

    return deps.every((depStepId) => {
      const depProgress = progress.find((p) => p.step_id === depStepId);
      return depProgress?.status === 'completed';
    });
  };

  // Get the names of incomplete dependencies for display
  const getIncompleteDependencies = (item: ProgressWithStep): string[] => {
    const deps = item.step.dependencies;
    if (!deps || deps.length === 0) return [];

    return deps
      .filter((depStepId) => {
        const depProgress = progress.find((p) => p.step_id === depStepId);
        return depProgress?.status !== 'completed';
      })
      .map((depStepId) => {
        const depProgress = progress.find((p) => p.step_id === depStepId);
        return depProgress?.step?.task_name || 'Unknown task';
      });
  };

  const groupedSteps = useMemo(() => {
    return categories
      .map((category) => {
        const steps = filteredProgress.filter((item) => item.step.category === category.slug);
        if (steps.length === 0) return null;
        const completed = steps.filter((item) => item.status === 'completed').length;
        return {
          category,
          steps,
          completed,
          total: steps.length,
        };
      })
      .filter(Boolean) as {
      category: StepCategoryRecord;
      steps: ProgressWithStep[];
      completed: number;
      total: number;
    }[];
  }, [categories, filteredProgress]);

  const openTasks = useMemo(() => tasks.filter((task) => task.status !== 'completed'), [tasks]);
  const completedTaskCount = tasks.length - openTasks.length;

  // Auto-expand categories that have incomplete tasks
  useEffect(() => {
    if (groupedSteps.length > 0) {
      const categoriesWithIncomplete = groupedSteps
        .filter(g => g.completed < g.total)
        .map(g => g.category.slug);
      if (categoriesWithIncomplete.length > 0) {
        setExpandedCategories(new Set(categoriesWithIncomplete));
      }
    }
  }, [groupedSteps]);

  const toggleCategory = (categorySlug: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categorySlug)) {
      newExpanded.delete(categorySlug);
    } else {
      newExpanded.add(categorySlug);
    }
    setExpandedCategories(newExpanded);
  };

  const handleStepStatusChange = async (progressId: string, status: StepStatus, keepModalOpen = false) => {
    if (!currentAdmin) {
      setToast({ message: 'You must be authenticated to update progress.', type: 'error' });
      return;
    }

    try {
      setUpdatingStepId(progressId);
      if (status === 'completed') {
        await api.adminCompleteStep(progressId, currentAdmin.id);
      } else {
        await api.updateProgressStatus(progressId, status);
      }
      await loadData();

      // Update selectedStep if modal is open and we want to keep it open
      if (keepModalOpen && selectedStep && selectedStep.id === progressId) {
        setSelectedStep(prev => prev ? { ...prev, status } : null);
      }
    } catch (err) {
      // Error handled
      setToast({ message: 'Failed to update step status. Please try again.', type: 'error' });
    } finally {
      setUpdatingStepId(null);
    }
  };

  const handleMarkComplete = async (progressId: string) => {
    if (!currentAdmin) return;
    try {
      await api.adminCompleteStep(progressId, currentAdmin.id);
      await loadData();
    } catch (error) {
      // Error handled silently
      setToast({ message: 'Failed to mark step as complete. Please try again.', type: 'error' });
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      setCompletingTaskId(taskId);
      await api.updateCustomTask(taskId, { status: 'completed', completed_date: new Date().toISOString() });
      await loadData();
    } catch (err) {
      // Error handled
      setToast({ message: 'Failed to complete task. Please try again.', type: 'error' });
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleCreateTask = async () => {
    if (!currentAdmin || !id) return;

    if (!newTask.title.trim()) {
      setToast({ message: 'Please enter a task title', type: 'error' });
      return;
    }

    try {
      await api.createCustomTask({
        loan_officer_id: id,
        title: newTask.title,
        description: newTask.description || undefined,
        priority: newTask.priority,
        due_date: newTask.due_date || undefined,
        assigned_by: currentAdmin.id,
      });

      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
      });
      setShowTaskModal(false);
      await loadData();
    } catch (error) {
      // Error handled silently
      setToast({ message: 'Failed to create task. Please try again.', type: 'error' });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const { error } = await supabase.from('custom_tasks').delete().eq('id', taskId);
      if (error) throw error;
      await loadData();
    } catch (error) {
      // Error handled silently
      setToast({ message: 'Failed to delete task. Please try again.', type: 'error' });
    }
  };

  const handleOpenStepModal = async () => {
    try {
      const { data: allSteps, error } = await supabase
        .from('onboarding_steps')
        .select('*')
        .eq('is_active', true)
        .order('sequential_order', { ascending: true });

      if (error) throw error;

      const assignedStepIds = progress.map((p) => p.step_id);
      const unassignedSteps = allSteps.filter((s) => !assignedStepIds.includes(s.id));

      setAvailableSteps(unassignedSteps);
      setShowStepModal(true);
    } catch (error) {
      // Error handled silently
      setToast({ message: 'Failed to load available steps. Please try again.', type: 'error' });
    }
  };

  const handleAssignStep = async () => {
    if (!id || !selectedStepId) return;

    try {
      await api.assignStepToLO(id, selectedStepId);
      setShowStepModal(false);
      setSelectedStepId('');
      await loadData();
    } catch (error: any) {
      // Error handled silently
      setToast({ message: error.message || 'Failed to assign step. Please try again.', type: 'error' });
    }
  };

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    if (!file.type.startsWith('image/')) {
      setToast({ message: 'Please upload an image file (PNG, JPG, etc.)', type: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: 'File size must be less than 5MB', type: 'error' });
      return;
    }

    try {
      setUploadingSignature(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${id}/signature.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('email-signatures')
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('email-signatures').getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ email_signature_url: publicUrl })
        .eq('id', id);

      if (updateError) throw updateError;

      setToast({ message: 'Email signature uploaded successfully!', type: 'success' });
      await loadData();
    } catch (error: any) {
      // Error handled silently
      setToast({ message: `Failed to upload signature: ${error.message}`, type: 'error' });
    } finally {
      setUploadingSignature(false);
      event.target.value = '';
    }
  };

  const handleHeadshotUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    if (!file.type.startsWith('image/')) {
      setToast({ message: 'Please upload an image file (PNG, JPG, etc.)', type: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: 'File size must be less than 5MB', type: 'error' });
      return;
    }

    try {
      setUploadingHeadshot(true);
      await api.uploadLOHeadshot(id, file);
      // Reload data to get the updated headshot
      await loadData();
    } catch (err: any) {
      console.error('Error uploading headshot:', err);
      setToast({ message: 'Failed to upload headshot. Please try again.', type: 'error' });
    } finally {
      setUploadingHeadshot(false);
      event.target.value = '';
    }
  };

  const handleIrsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setToast({ message: 'Please upload a PDF or image file (PDF, JPG, PNG)', type: 'error' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setToast({ message: 'File size must be less than 10MB', type: 'error' });
      return;
    }

    try {
      setUploadingIrs(true);

      const ext = file.name.split('.').pop() || 'pdf';
      const fileName = `${id}/irs_paperwork-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('onboarding-documents')
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      // Check if a2p_registrations row exists (may not if LO hasn't gone through onboarding form yet)
      const { data: existingA2P } = await supabase
        .from('a2p_registrations')
        .select('id')
        .eq('loan_officer_id', id)
        .maybeSingle();

      if (existingA2P) {
        const { error: updateError } = await supabase
          .from('a2p_registrations')
          .update({ irs_paperwork: fileName })
          .eq('loan_officer_id', id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('a2p_registrations')
          .insert({ loan_officer_id: id, irs_paperwork: fileName });
        if (insertError) throw insertError;
      }

      setToast({ message: 'IRS paperwork uploaded successfully!', type: 'success' });
      await loadData();
      // Reload file URLs
      setFileUrls({});
    } catch (error: any) {
      console.error('Error uploading IRS paperwork:', error);
      setToast({ message: `Failed to upload IRS paperwork: ${error.message}`, type: 'error' });
    } finally {
      setUploadingIrs(false);
      event.target.value = '';
    }
  };

  const handleDownloadSignature = () => {
    if (!loanOfficer?.email_signature_url) return;
    window.open(loanOfficer.email_signature_url, '_blank');
  };

  const getHeadshotExtension = (url: string, mimeType?: string | null) => {
    try {
      const { pathname } = new URL(url);
      const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
      if (match) return match[1];
    } catch (error) {
      // Parse error handled
    }

    if (mimeType?.includes('/')) {
      return mimeType.split('/')[1];
    }

    return 'png';
  };

  const handleDownloadHeadshot = async (headshotUrl?: string | null) => {
    const url = headshotUrl || loanOfficer?.headshot;
    if (!url) return;

    const safeName = [loanOfficer?.first_name, loanOfficer?.last_name].filter(Boolean).join('-') || 'headshot';

    // Helper to get signed URL - handles both storage paths and full URLs
    const getSignedUrlForPath = async (path: string): Promise<string | null> => {
      if (!path.startsWith('http') && path.includes('/')) {
        // It's a storage path, use api.getSignedUrl with bucket
        return api.getSignedUrl('onboarding-documents', path);
      }
      return getSignedUrl(path);
    };

    try {
      // Get signed URL for private storage files
      const signedUrl = await getSignedUrlForPath(url);
      if (!signedUrl) {
        throw new Error('Failed to get signed URL');
      }

      const response = await fetch(signedUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch headshot: ${response.status}`);
      }

      const blob = await response.blob();
      const extension = getHeadshotExtension(url, blob.type);
      const filename = `${safeName.replace(/\s+/g, '-').toLowerCase()}-headshot.${extension}`;

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback: try to open signed URL in new tab
      const signedUrl = await getSignedUrlForPath(url);
      window.open(signedUrl || url, '_blank');
    }
  };

  const copyToClipboard = (text: string, sectionName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSection(sectionName);
      setTimeout(() => setCopiedSection(null), 2000);
    });
  };

  const trimOrEmpty = (value?: string | null) => (value ? value.trim() : '');
  const toStringValue = (value?: string | number | null) => {
    if (value === null || value === undefined) return '';
    return typeof value === 'number' ? value.toString() : value.trim();
  };

  const resolvedFullName =
    trimOrEmpty(loanOfficer?.full_name) ||
    [loanOfficer?.first_name, loanOfficer?.last_name].filter(Boolean).join(' ').trim();
  const resolvedNmls = toStringValue(loanOfficer?.nmls_number) || toStringValue(loanOfficer?.nmls);
  const phoneValue = loanOfficer?.phone || loanOfficer?.cell_number;
  const resolvedA2PPhone = phoneValue ? formatPhone(phoneValue).replace('—', '') : '';
  const resolvedHeadshot = trimOrEmpty(loanOfficer?.headshot);

  const signatureHelperFields = [
    {
      key: 'signature_fullName',
      label: 'Full Name',
      value: resolvedFullName,
    },
    {
      key: 'signature_email',
      label: 'Email',
      value: trimOrEmpty(loanOfficer?.email),
    },
    {
      key: 'signature_nmls',
      label: 'NMLS',
      value: resolvedNmls,
    },
    {
      key: 'signature_a2pPhone',
      label: 'A2P Phone',
      value: resolvedA2PPhone,
    },
    {
      key: 'signature_headshot',
      label: 'Headshot',
      value: resolvedHeadshot,
      isHeadshot: true,
    },
    {
      key: 'signature_workLocation',
      label: 'Work Location',
      value: loanOfficer?.place_of_work || '',
    },
  ];

  const renderStatusBadge = (status: StepStatus) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-2xl text-xs font-semibold ${statusColors[status]}`}>
      {statusLabels[status]}
    </span>
  );

  const onboardingStatus = getOnboardingStatus(summary || undefined);

  if (loading) {
    return (
      <AdminShell title="Loan Officers" description="Loading case file..." nav={navigation}>
        <div className="flex items-center justify-center py-32 text-slate-400">
          <div className="h-12 w-12 rounded-full border-2 border-[#cde3f3] border-t-lumin-primary animate-spin" />
        </div>
      </AdminShell>
    );
  }

  if (error) {
    return (
      <AdminShell title="Loan Officers" description="Case file unavailable" nav={navigation}>
        <div className="rounded-3xl border border-rose-100 bg-rose-50 px-6 py-8 text-center text-slate-700">
          <p className="text-base font-semibold text-rose-700">{error}</p>
          <p className="mt-2 text-sm text-rose-500">Return to the roster and retry.</p>
          <Link
            to="/admin/loan-officers"
            className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-lumin-primary px-4 py-2 text-sm font-semibold text-white"
          >
            Back to roster
          </Link>
        </div>
      </AdminShell>
    );
  }

  if (!loanOfficer) {
    return (
      <AdminShell title="Loan Officers" description="Case file unavailable" nav={navigation}>
        <div className="rounded-3xl border border-slate-100 bg-white px-6 py-10 text-center text-slate-500">
          Unable to locate this loan officer.
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title="Loan Officer Detail"
      description="Track progress, unblock tasks, and finalize compliance signals."
      nav={navigation}
      actions={
        <div className="flex items-center gap-3">
          {id && <HandoffButton loanOfficerId={id} />}
          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20 disabled:opacity-70"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      }
    >
      <div className="space-y-8">
        {/* Back button and header actions */}
        <div className="flex items-center justify-between">
          <Link
            to="/admin/loan-officers"
            className="inline-flex items-center gap-2 text-sm font-semibold text-lumin-primary hover:text-[#014f70]"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to roster
          </Link>

          <div className="flex gap-3">
            {/* View As button - admins only */}
            {isAdmin && id && (
              <button
                onClick={async () => {
                  await startImpersonation(id);
                  navigate('/onboarding');
                }}
                className="inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
              >
                <EyeIcon className="h-5 w-5" />
                View As LO
              </button>
            )}
            <button
              onClick={handleOpenStepModal}
              className="inline-flex items-center gap-2 rounded-2xl bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <ClipboardDocumentListIcon className="h-5 w-5" />
              Assign Step
            </button>
            <button
              onClick={() => setShowTaskModal(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-lumin-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#014f70]"
            >
              <PlusIcon className="h-5 w-5" />
              Assign Task
            </button>
            {a2pBusinessRegistration && a2pBusinessRegistration.business_name && (
              <button
                onClick={() => setShowA2PSOPModal(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <DocumentDuplicateIcon className="h-5 w-5" />
                A2P SOP
              </button>
            )}
          </div>
        </div>

        {/* LO Header Card */}
        <section className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-6">
              {/* Clickable headshot with upload capability */}
              <button
                type="button"
                onClick={() => headshotInputRef.current?.click()}
                disabled={uploadingHeadshot}
                className="relative group cursor-pointer"
                title="Click to change photo"
              >
                <SignedImage
                  src={loanOfficer.headshot}
                  alt={`${loanOfficer.first_name} ${loanOfficer.last_name}`}
                  className="h-20 w-20 rounded-full object-cover border-2 border-slate-100 group-hover:opacity-75 transition-opacity"
                  fallback={
                    <div className="h-20 w-20 rounded-full bg-lumin-primary flex items-center justify-center text-white font-bold text-2xl group-hover:opacity-75 transition-opacity">
                      {loanOfficer.first_name?.charAt(0)}
                      {loanOfficer.last_name?.charAt(0)}
                    </div>
                  }
                />
                {/* Hover overlay with camera icon */}
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                  {uploadingHeadshot ? (
                    <div className="h-6 w-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CameraIcon className="h-6 w-6 text-white" />
                  )}
                </div>
                {/* Hidden file input */}
                <input
                  ref={headshotInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleHeadshotUpload}
                  disabled={uploadingHeadshot}
                  className="hidden"
                />
              </button>

              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Loan Officer</p>
                <h1 className="text-4xl font-semibold text-slate-900 mt-2">
                  {loanOfficer.first_name} {loanOfficer.last_name}
                </h1>
                <p className="text-sm uppercase tracking-[0.3em] text-slate-400 mt-1">
                  {loanOfficer.title || 'Loan Officer'}
                </p>

                <div className="mt-4">
                  {renderStatusBadge(onboardingStatus)}
                </div>

                <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="h-4 w-4 text-slate-400" />
                    {loanOfficer.email || 'No email on file'}
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4 text-slate-400" />
                    <div>
                      {loanOfficer.cell_number && <div>C: {formatPhone(loanOfficer.cell_number)}</div>}
                      {loanOfficer.phone && <div>W: {formatPhone(loanOfficer.phone)}</div>}
                      {!loanOfficer.cell_number && !loanOfficer.phone && <span>No phone yet</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {summary && (
              <div className="rounded-3xl border border-slate-100 bg-[#f4f7fb] p-6 w-full max-w-md">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Trajectory</p>
                <p className="text-5xl font-semibold text-slate-900 mt-2">{summary.completion_percentage.toFixed(0)}%</p>
                <p className="text-sm text-slate-500">
                  {summary.completed_steps} / {summary.total_steps} steps locked in
                </p>
                <div className="mt-4 h-3 rounded-full bg-white">
                  <div
                    className="h-3 rounded-full bg-lumin-primary"
                    style={{ width: `${summary.completion_percentage}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('steps')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'steps'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Onboarding Steps
            {summary && summary.in_progress_steps > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-lumin-primary text-white">
                {summary.in_progress_steps}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'tasks'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Custom Tasks
            {openTasks.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white">
                {openTasks.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('a2p')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'a2p'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            A2P Registration
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Activity Log
          </button>
          <button
            onClick={() => setActiveTab('sponsorship')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'sponsorship'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <MapPinIcon className="h-4 w-4 inline mr-1" />
            Sponsorship
            {sponsorships.filter(s => s.status === 'accepted').length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-500 text-white">
                {sponsorships.filter(s => s.status === 'accepted').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'files'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FolderIcon className="h-4 w-4 inline mr-1" />
            Files
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'notes'
                ? 'border-lumin-primary text-lumin-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 inline mr-1" />
            Notes
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Multi-segment progress bar */}
            {summary && (
              <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-900 mb-4">Progress Breakdown</h2>
                <div className="flex h-6 rounded-full overflow-hidden bg-slate-100">
                  {summary.completed_steps > 0 && (
                    <div
                      className="bg-emerald-500 flex items-center justify-center text-xs font-semibold text-white"
                      style={{ width: `${(summary.completed_steps / summary.total_steps) * 100}%` }}
                      title={`${summary.completed_steps} completed`}
                    >
                      {summary.completed_steps > 0 && summary.completed_steps}
                    </div>
                  )}
                  {summary.in_progress_steps > 0 && (
                    <div
                      className="bg-lumin-primary flex items-center justify-center text-xs font-semibold text-white"
                      style={{ width: `${(summary.in_progress_steps / summary.total_steps) * 100}%` }}
                      title={`${summary.in_progress_steps} in progress`}
                    >
                      {summary.in_progress_steps > 0 && summary.in_progress_steps}
                    </div>
                  )}
                  {summary.blocked_steps > 0 && (
                    <div
                      className="bg-amber-500 flex items-center justify-center text-xs font-semibold text-white"
                      style={{ width: `${(summary.blocked_steps / summary.total_steps) * 100}%` }}
                      title={`${summary.blocked_steps} blocked`}
                    >
                      {summary.blocked_steps > 0 && summary.blocked_steps}
                    </div>
                  )}
                  {summary.not_started_steps > 0 && (
                    <div
                      className="bg-slate-300 flex items-center justify-center text-xs font-semibold text-slate-700"
                      style={{ width: `${(summary.not_started_steps / summary.total_steps) * 100}%` }}
                      title={`${summary.not_started_steps} not started`}
                    >
                      {summary.not_started_steps > 0 && summary.not_started_steps}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Stats cards */}
            {summary && (
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Completed</p>
                  <p className="text-3xl font-semibold text-emerald-600 mt-2">{summary.completed_steps}</p>
                </div>
                <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">In Progress</p>
                  <p className="text-3xl font-semibold text-lumin-primary mt-2">{summary.in_progress_steps}</p>
                </div>
                <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Blocked</p>
                  <p className="text-3xl font-semibold text-amber-600 mt-2">{summary.blocked_steps}</p>
                </div>
                <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Last signal</p>
                  <p className="text-3xl font-semibold text-slate-900 mt-2">
                    {formatRelativeDate(summary.latest_completion_date || summary.onboarding_start_date)}
                  </p>
                </div>
              </section>
            )}

            {/* Category Progress Cards */}
            {groupedSteps.length > 0 && (
              <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Progress by Category</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedSteps.map(({ category, steps, completed, total }) => {
                    const percentage = (completed / total) * 100;
                    const blockedCount = steps.filter((s) => s.status === 'blocked').length;
                    const inProgressCount = steps.filter((s) => s.status === 'in_progress').length;

                    return (
                      <div
                        key={category.id}
                        className="rounded-2xl border-2 p-5 hover:shadow-md transition-shadow"
                        style={{ borderColor: category.accent_color }}
                      >
                        <div className="mb-3">
                          <h3 className="font-semibold text-slate-900">{category.name}</h3>
                        </div>
                        <div className="flex items-baseline justify-between mb-2">
                          <span className="text-3xl font-bold text-slate-900">{percentage.toFixed(0)}%</span>
                          <span className="text-sm text-slate-600">
                            {completed}/{total}
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%`, backgroundColor: category.color }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <CheckCircleIcon className="h-3 w-3 text-emerald-600" />
                            <span>{completed} done</span>
                          </div>
                          {inProgressCount > 0 && (
                            <div className="flex items-center gap-1">
                              <PlayIcon className="h-3 w-3 text-lumin-primary" />
                              <span>{inProgressCount} active</span>
                            </div>
                          )}
                          {blockedCount > 0 && (
                            <div className="flex items-center gap-1">
                              <XMarkIcon className="h-3 w-3 text-amber-600" />
                              <span>{blockedCount} blocked</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Steps Tab */}
        {activeTab === 'steps' && (
          <div className="space-y-8">
            {/* Email Signature Section */}
            <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Email Signature</h2>
                  <p className="text-sm text-slate-500 mt-1">Upload or manage the loan officer's email signature</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Quick Copy Fields */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700">Quick Copy Fields</h3>
                    <p className="text-xs text-slate-500">Tap to copy values for signature creation</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {signatureHelperFields.map((field) => (
                      <div
                        key={field.key}
                        className="border border-slate-200 rounded-2xl bg-slate-50 px-4 py-3 flex items-start justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{field.label}</p>
                          {field.isHeadshot && field.value ? (
                            <div className="flex items-center gap-2 mt-1">
                              <SignedImage
                                src={field.value}
                                alt={`${loanOfficer.first_name} ${loanOfficer.last_name} headshot`}
                                className="h-8 w-8 rounded-full object-cover border border-slate-200 flex-shrink-0"
                                fallback={
                                  <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 text-xs border border-slate-200 flex-shrink-0">
                                    ?
                                  </div>
                                }
                              />
                              <p className="text-xs text-slate-500 italic">Headshot uploaded</p>
                            </div>
                          ) : (
                            <p
                              className={`text-sm mt-1 ${field.value ? 'text-slate-900 truncate' : 'text-slate-400 italic'}`}
                              title={field.value || 'Not provided yet'}
                            >
                              {field.value || 'Not provided yet'}
                            </p>
                          )}
                        </div>
                        {field.isHeadshot ? (
                          <button
                            type="button"
                            onClick={() => field.value && handleDownloadHeadshot(field.value)}
                            disabled={!field.value}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold ${
                              field.value
                                ? 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-100'
                                : 'text-slate-400 bg-slate-100 border border-slate-200 cursor-not-allowed'
                            }`}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Download
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => field.value && copyToClipboard(field.value, field.key)}
                            disabled={!field.value}
                            className={`inline-flex items-center justify-center p-2 rounded-xl ${
                              field.value
                                ? 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-100'
                                : 'text-slate-400 bg-slate-100 border border-slate-200 cursor-not-allowed'
                            }`}
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                            {copiedSection === field.key && (
                              <span className="ml-1 text-xs font-semibold text-lumin-primary">✓</span>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Licensed States Quick Copy */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-700">Licensed States</h3>
                    <p className="text-xs text-slate-500">Tap to copy all states</p>
                  </div>
                  {linkedStateLicenses && linkedStateLicenses.length > 0 ? (
                    <div className="border border-slate-200 rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                            {linkedStateLicenses.length} State{linkedStateLicenses.length !== 1 ? 's' : ''}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {[...linkedStateLicenses]
                              .sort((a, b) => (STATE_ABBREVIATIONS[a] || a).localeCompare(STATE_ABBREVIATIONS[b] || b))
                              .map((state) => (
                                <span
                                  key={state}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-lumin-primary/10 text-lumin-primary text-xs font-medium"
                                >
                                  {STATE_ABBREVIATIONS[state] || state}
                                </span>
                              ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const statesText = [...linkedStateLicenses]
                              .sort((a, b) => (STATE_ABBREVIATIONS[a] || a).localeCompare(STATE_ABBREVIATIONS[b] || b))
                              .map((s) => STATE_ABBREVIATIONS[s] || s)
                              .join(', ');
                            copyToClipboard(statesText, 'licensed_states');
                          }}
                          className="inline-flex items-center justify-center p-2 rounded-xl text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 flex-shrink-0"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                          {copiedSection === 'licensed_states' && (
                            <span className="ml-1 text-xs font-semibold text-lumin-primary">✓</span>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-sm text-slate-400 italic">No licensed states on file</p>
                    </div>
                  )}
                </div>

                {/* Signature Upload/Preview */}
                {loanOfficer?.email_signature_url ? (
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="rounded-2xl border-2 border-slate-200 p-4 bg-slate-50">
                        <img
                          src={loanOfficer.email_signature_url}
                          alt="Email Signature"
                          className="max-w-full h-auto"
                          style={{ maxHeight: '200px' }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleDownloadSignature}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold rounded-2xl"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                        Download
                      </button>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-lumin-primary hover:bg-[#014f70] text-white font-semibold rounded-2xl cursor-pointer">
                        <ArrowUpTrayIcon className="h-5 w-5" />
                        {uploadingSignature ? 'Uploading...' : 'Replace'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleSignatureUpload}
                          disabled={uploadingSignature}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center">
                    <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-slate-400" />
                    <p className="mt-2 text-sm text-slate-600">No email signature uploaded yet</p>
                    <div className="mt-4 flex gap-2 justify-center">
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-lumin-primary hover:bg-[#014f70] text-white font-semibold rounded-2xl cursor-pointer">
                        <ArrowUpTrayIcon className="h-5 w-5" />
                        {uploadingSignature ? 'Uploading...' : 'Upload Signature'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleSignatureUpload}
                          disabled={uploadingSignature}
                          className="hidden"
                        />
                      </label>
                      <a
                        href="https://www.canva.com/design/DAGx_HlDicU/BZgTdJ8g3XW6-ck6arZBeA/edit"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-2xl"
                      >
                        Create Signature
                      </a>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">PNG, JPG up to 5MB</p>
                  </div>
                )}
              </div>
            </section>

            {/* Section Toggle */}
            <section className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-slate-700">Section:</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStepSection('onboarding')}
                    className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors ${
                      stepSection === 'onboarding'
                        ? 'bg-lumin-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    Onboarding
                  </button>
                  <button
                    onClick={() => setStepSection('hands_on')}
                    className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors ${
                      stepSection === 'hands_on'
                        ? 'bg-lumin-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    Hands On
                  </button>
                </div>
              </div>
            </section>

            {/* Target Audience Filter */}
            <section className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-sm font-semibold text-slate-700">Filter by Responsibility:</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTargetAudienceFilter('all')}
                    className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors ${
                      targetAudienceFilter === 'all'
                        ? 'bg-lumin-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    All Tasks ({isVA ? progress.filter((p) => p.step.section === stepSection && p.step.target_audience !== 'admin').length : progress.filter((p) => p.step.section === stepSection).length})
                  </button>
                  {!isVA && (
                    <button
                      onClick={() => setTargetAudienceFilter('admin')}
                      className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors ${
                        targetAudienceFilter === 'admin'
                          ? 'bg-purple-600 text-white'
                          : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                      }`}
                    >
                      Admin Tasks ({progress.filter((p) => p.step.section === stepSection && p.step.target_audience === 'admin').length})
                    </button>
                  )}
                  <button
                    onClick={() => setTargetAudienceFilter('loan_officer')}
                    className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors ${
                      targetAudienceFilter === 'loan_officer'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                  >
                    LO Tasks ({progress.filter((p) => p.step.section === stepSection && p.step.target_audience === 'loan_officer').length})
                  </button>
                  <button
                    onClick={() => setTargetAudienceFilter('va')}
                    className={`px-4 py-2 text-sm font-semibold rounded-2xl transition-colors ${
                      targetAudienceFilter === 'va'
                        ? 'bg-teal-600 text-white'
                        : 'bg-teal-100 text-teal-800 hover:bg-teal-200'
                    }`}
                  >
                    VA Tasks ({progress.filter((p) => p.step.section === stepSection && p.step.target_audience === 'va').length})
                  </button>
                </div>
              </div>
            </section>

            {/* Category Accordion */}
            {groupedSteps.length === 0 ? (
              <div className="rounded-3xl border border-slate-100 bg-white p-6 text-sm text-slate-500">
                No onboarding steps are assigned to this loan officer yet.
              </div>
            ) : (
              <div className="space-y-4">
                {groupedSteps.map(({ category, steps, completed, total }) => {
                  const isExpanded = expandedCategories.has(category.slug);
                  const hasInProgress = steps.some((s) => s.status === 'in_progress');
                  const hasBlocked = steps.some((s) => s.status === 'blocked');
                  const percentage = (completed / total) * 100;

                  return (
                    <div key={category.id} className="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category.slug)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDownIcon className="h-5 w-5 text-slate-500" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-slate-500" />
                            )}
                            <h3 className="text-lg font-semibold text-slate-900">{category.name}</h3>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600">
                              {completed} of {total} completed
                            </span>
                            {hasInProgress && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-xl bg-blue-100 text-blue-800">
                                In Progress
                              </span>
                            )}
                            {hasBlocked && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-xl bg-amber-100 text-amber-800">
                                Blocked
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%`, backgroundColor: category.color }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-slate-900 w-12 text-right">
                            {percentage.toFixed(0)}%
                          </span>
                        </div>
                      </button>

                      {/* Category Steps */}
                      {isExpanded && (
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {steps.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => setSelectedStep(item)}
                                className={`rounded-2xl border p-4 cursor-pointer transition-all ${
                                  item.status === 'in_progress'
                                    ? 'border-amber-200 bg-amber-50 hover:border-amber-300'
                                    : item.status === 'completed'
                                    ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-300'
                                    : 'border-slate-200 bg-white hover:shadow-md hover:border-lumin-primary'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  {/* Step Icon */}
                                  <div className="flex-shrink-0">
                                    {item.status === 'completed' ? (
                                      <CheckCircleSolid className="h-6 w-6 text-emerald-500" />
                                    ) : (
                                      <div className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                                        {item.step.sequential_order}
                                      </div>
                                    )}
                                  </div>

                                  {/* Step Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-slate-900">{item.step.task_name}</h4>
                                        <div className="flex flex-wrap items-center gap-2 mt-1">
                                          {item.step.target_audience === 'admin' ? (
                                            <span className="px-2 py-0.5 text-xs font-semibold rounded-xl bg-purple-100 text-purple-800 border border-purple-300">
                                              Admin Task
                                            </span>
                                          ) : item.step.target_audience === 'va' ? (
                                            <span className="px-2 py-0.5 text-xs font-semibold rounded-xl bg-teal-100 text-teal-800 border border-teal-300">
                                              VA Task
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 text-xs font-semibold rounded-xl bg-blue-100 text-blue-800 border border-blue-300">
                                              LO Task
                                            </span>
                                          )}
                                          <span
                                            className={`px-2 py-0.5 text-xs font-semibold rounded-xl ${statusColors[item.status]}`}
                                          >
                                            {statusLabels[item.status]}
                                          </span>
                                          {item.status === 'not_started' && !areDependenciesMet(item) && (
                                            <span className="px-2 py-0.5 text-xs font-semibold rounded-xl bg-amber-100 text-amber-700 border border-amber-200">
                                              Waiting
                                            </span>
                                          )}
                                        </div>
                                        {item.status === 'not_started' && !areDependenciesMet(item) && (
                                          <p className="text-xs text-amber-600 mt-1">
                                            Depends on: {getIncompleteDependencies(item).join(', ')}
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {item.notes && (
                                      <div className="mb-3 p-2 bg-blue-50 rounded-xl">
                                        <p className="text-xs font-semibold text-blue-900">LO Notes:</p>
                                        <p className="text-xs text-blue-800 mt-1">{item.notes}</p>
                                      </div>
                                    )}

                                    {/* Admin Actions */}
                                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                      {item.status === 'not_started' && (
                                        <button
                                          onClick={() => setSelectedStep(item)}
                                          className="px-4 py-2 rounded-2xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68] transition"
                                        >
                                          Start
                                        </button>
                                      )}
                                      {item.status === 'in_progress' && (
                                        <button
                                          onClick={() => handleMarkComplete(item.id)}
                                          disabled={updatingStepId === item.id}
                                          className="px-4 py-2 rounded-2xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-60"
                                        >
                                          Complete
                                        </button>
                                      )}
                                      {item.status === 'completed' && (
                                        <button
                                          onClick={() => handleStepStatusChange(item.id, 'not_started')}
                                          disabled={updatingStepId === item.id}
                                          className="px-4 py-2 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-60"
                                        >
                                          Undo
                                        </button>
                                      )}
                                      {item.status === 'blocked' && (
                                        <button
                                          onClick={() => handleStepStatusChange(item.id, 'in_progress')}
                                          disabled={updatingStepId === item.id}
                                          className="px-4 py-2 rounded-2xl text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-60"
                                        >
                                          Unblock
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-6">
            {/* Custom Tasks */}
            <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Admin Tasks</p>
                  <p className="text-xs text-slate-500">
                    {openTasks.length} open · {completedTaskCount} completed
                  </p>
                </div>
                <PlayIcon className="h-5 w-5 text-lumin-primary" />
              </div>
              <div className="mt-4 space-y-3">
                {tasks.length === 0 ? (
                  <p className="text-sm text-slate-500">No tasks assigned yet.</p>
                ) : (
                  tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`rounded-2xl border p-4 ${
                        task.status === 'completed'
                          ? 'border-slate-100 bg-slate-50'
                          : 'border-amber-100 bg-amber-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-slate-900">{task.title}</p>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-xl ${priorityColors[task.priority]}`}>
                              {task.priority.toUpperCase()}
                            </span>
                          </div>
                          {task.description && <p className="text-xs text-slate-600 mt-1">{task.description}</p>}
                          {task.due_date && (
                            <p className="text-xs text-amber-600 mt-1">Due {formatLocalDate(task.due_date)}</p>
                          )}
                          <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-semibold rounded-xl ${statusColors[task.status]}`}>
                            {statusLabels[task.status]}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {task.status !== 'completed' && (
                            <button
                              onClick={() => handleCompleteTask(task.id)}
                              disabled={completingTaskId === task.id}
                              className="rounded-full p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-60"
                              title="Mark complete"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="rounded-full p-1.5 text-rose-600 hover:bg-rose-50"
                            title="Delete task"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}

        {/* A2P Tab */}
        {activeTab === 'a2p' && (
          <div className="space-y-6">
            {/* A2P Compliance */}
            <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">A2P Compliance</p>
                  <p className="text-xs text-slate-500">
                    {a2pRegistration ? 'Tracking brand + campaign approvals' : 'No phone registration on file'}
                  </p>
                </div>
              </div>
              {a2pRegistration ? (
                <div className="mt-4 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-100 bg-[#f4fbff] p-4">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Brand</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Status</p>
                      <span
                        className={`mt-2 inline-flex items-center rounded-2xl border px-3 py-1 text-xs font-semibold ${
                          a2pStatusColors[a2pRegistration.brand_status]
                        }`}
                      >
                        {a2pStatusLabels[a2pRegistration.brand_status]}
                      </span>
                      {a2pRegistration.brand_reference_id && (
                        <p className="mt-2 text-xs text-slate-500">Ref: {a2pRegistration.brand_reference_id}</p>
                      )}
                      {a2pRegistration.brand_approved_at && (
                        <p className="mt-2 text-xs text-green-600">Approved: {new Date(a2pRegistration.brand_approved_at).toLocaleDateString()}</p>
                      )}
                      {a2pRegistration.brand_failure_reason && (
                        <p className="mt-2 text-xs text-rose-600">{a2pRegistration.brand_failure_reason}</p>
                      )}
                    </div>
                    <div className="rounded-2xl border border-slate-100 bg-[#f4fbff] p-4">
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Campaign</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">Status</p>
                      <span
                        className={`mt-2 inline-flex items-center rounded-2xl border px-3 py-1 text-xs font-semibold ${
                          a2pStatusColors[a2pRegistration.campaign_status]
                        }`}
                      >
                        {a2pStatusLabels[a2pRegistration.campaign_status]}
                      </span>
                      {a2pRegistration.campaign_reference_id && (
                        <p className="mt-2 text-xs text-slate-500">Ref: {a2pRegistration.campaign_reference_id}</p>
                      )}
                      {a2pRegistration.campaign_approved_at && (
                        <p className="mt-2 text-xs text-green-600">Approved: {new Date(a2pRegistration.campaign_approved_at).toLocaleDateString()}</p>
                      )}
                      {a2pRegistration.campaign_failure_reason && (
                        <p className="mt-2 text-xs text-rose-600">{a2pRegistration.campaign_failure_reason}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Last updated {formatRelativeDate(a2pRegistration.updated_at)}</p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">No submissions yet.</p>
              )}

              {a2pBusinessRegistration && (
                <div className="mt-6 rounded-2xl border border-slate-100 bg-[#fef8f0] p-4 text-sm text-slate-700 space-y-1">
                  <p className="font-semibold text-slate-900">Business Profile</p>
                  <p>{a2pBusinessRegistration.business_name || '—'}</p>
                  <p>
                    {[a2pBusinessRegistration.street_address, a2pBusinessRegistration.city, a2pBusinessRegistration.state]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {a2pBusinessRegistration.ein && <p>EIN: {a2pBusinessRegistration.ein}</p>}
                </div>
              )}

              {/* IRS Paperwork */}
              <div className="mt-6 rounded-2xl border border-slate-100 bg-[#f9fbff] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">IRS Paperwork</p>
                      <p className="text-xs text-slate-500">A2P registration document</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {fileUrls.irsUrl ? (
                      <>
                        <a
                          href={fileUrls.irsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 text-xs font-semibold rounded-xl bg-lumin-primary text-white hover:bg-[#014c68]"
                        >
                          <EyeIcon className="h-4 w-4 inline mr-1" />
                          View
                        </a>
                        <a
                          href={fileUrls.irsUrl}
                          download
                          className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                          Download
                        </a>
                      </>
                    ) : null}
                    <input
                      type="file"
                      ref={irsInputRef}
                      onChange={handleIrsUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                    />
                    <button
                      onClick={() => irsInputRef.current?.click()}
                      disabled={uploadingIrs}
                      className="px-3 py-2 text-xs font-semibold rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50"
                    >
                      {uploadingIrs ? 'Uploading...' : fileUrls.irsUrl ? 'Replace' : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Sponsorship Tab */}
        {activeTab === 'sponsorship' && (
          <div className="space-y-6">
            <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">State Sponsorships</h2>
                <span className="text-sm text-slate-500">
                  {sponsorships.filter(s => s.status === 'accepted').length} of {sponsorships.length} accepted
                </span>
              </div>

              {sponsorships.length === 0 ? (
                <div className="text-center py-12">
                  <MapPinIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No sponsorship records yet.</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Sponsorships will appear here when approval emails are processed.
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          State
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          NMLS
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {sponsorships.map((sponsorship) => (
                        <tr key={sponsorship.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-lumin-primary/10 text-lumin-primary font-semibold text-xs mr-3">
                                {sponsorship.state_code}
                              </span>
                              <span className="text-sm font-medium text-slate-900">
                                {sponsorship.state_name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sponsorshipStatusColors[sponsorship.status]}`}>
                              {sponsorship.status === 'accepted' && (
                                <CheckCircleSolid className="h-3.5 w-3.5 mr-1" />
                              )}
                              {sponsorshipStatusLabels[sponsorship.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                            {sponsorship.accepted_at
                              ? formatLocalDate(sponsorship.accepted_at)
                              : sponsorship.rejected_at
                              ? formatLocalDate(sponsorship.rejected_at)
                              : formatLocalDate(sponsorship.created_at)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                            {sponsorship.nmls_id || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            {/* Activity Log */}
            <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-900">Latest Signals</p>
                <span className="text-xs text-slate-400">{activityLog.length} entries</span>
              </div>
              <div className="mt-4 space-y-3">
                {activityLog.length === 0 ? (
                  <p className="text-sm text-slate-500">No activity recorded yet.</p>
                ) : (
                  activityLog.slice(0, 6).map((log) => {
                    const context =
                      log.step?.task_name || log.task?.title || log.details?.description || log.action_type.replace(/_/g, ' ');
                    return (
                      <div key={log.id} className="rounded-2xl border border-slate-100 bg-[#f9fbff] p-4">
                        <p className="text-sm font-semibold text-slate-900 capitalize">{context}</p>
                        <p className="text-xs text-slate-500">{log.action_type.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatRelativeDate(log.created_at)}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <div className="space-y-6">
            <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Documents & Files</h2>
                  <p className="text-sm text-slate-500 mt-1">View and download uploaded documents for this loan officer</p>
                </div>
                <button
                  onClick={loadFileUrls}
                  disabled={loadingFiles}
                  className="px-4 py-2 text-sm font-semibold rounded-2xl bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-4 w-4 inline mr-1 ${loadingFiles ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {loadingFiles ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 border-2 border-slate-200 border-t-lumin-primary rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid gap-4">
                  {/* W9 Document */}
                  <div className="rounded-2xl border border-slate-100 bg-[#f9fbff] p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                          <DocumentTextIcon className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">W9 Form</p>
                          <p className="text-xs text-slate-500">Tax identification document</p>
                        </div>
                      </div>
                      {fileUrls.w9Url ? (
                        <div className="flex gap-2">
                          <a
                            href={fileUrls.w9Url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-lumin-primary text-white hover:bg-[#014c68]"
                          >
                            <EyeIcon className="h-4 w-4 inline mr-1" />
                            View
                          </a>
                          <a
                            href={fileUrls.w9Url}
                            download
                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                            Download
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not uploaded</span>
                      )}
                    </div>
                  </div>

                  {/* Headshot */}
                  <div className="rounded-2xl border border-slate-100 bg-[#f9fbff] p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                          <PhotoIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Headshot</p>
                          <p className="text-xs text-slate-500">Profile photo</p>
                        </div>
                      </div>
                      {fileUrls.headshotUrl ? (
                        <div className="flex gap-2">
                          <a
                            href={fileUrls.headshotUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-lumin-primary text-white hover:bg-[#014c68]"
                          >
                            <EyeIcon className="h-4 w-4 inline mr-1" />
                            View
                          </a>
                          <a
                            href={fileUrls.headshotUrl}
                            download
                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                            Download
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not uploaded</span>
                      )}
                    </div>
                  </div>

                  {/* Email Signature */}
                  <div className="rounded-2xl border border-slate-100 bg-[#f9fbff] p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <EnvelopeIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Email Signature</p>
                          <p className="text-xs text-slate-500">Signature image for emails</p>
                        </div>
                      </div>
                      {fileUrls.signatureUrl ? (
                        <div className="flex gap-2">
                          <a
                            href={fileUrls.signatureUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-lumin-primary text-white hover:bg-[#014c68]"
                          >
                            <EyeIcon className="h-4 w-4 inline mr-1" />
                            View
                          </a>
                          <a
                            href={fileUrls.signatureUrl}
                            download
                            className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                            Download
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not uploaded</span>
                      )}
                    </div>
                  </div>

                  {/* IRS Paperwork */}
                  <div className="rounded-2xl border border-slate-100 bg-[#f9fbff] p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                          <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">IRS Paperwork</p>
                          <p className="text-xs text-slate-500">A2P registration document</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {fileUrls.irsUrl ? (
                          <>
                            <a
                              href={fileUrls.irsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 text-xs font-semibold rounded-xl bg-lumin-primary text-white hover:bg-[#014c68]"
                            >
                              <EyeIcon className="h-4 w-4 inline mr-1" />
                              View
                            </a>
                            <a
                              href={fileUrls.irsUrl}
                              download
                              className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                              Download
                            </a>
                          </>
                        ) : null}
                        <input
                          type="file"
                          onChange={handleIrsUpload}
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          className="hidden"
                          id="irs-upload-files-tab"
                        />
                        <button
                          onClick={() => document.getElementById('irs-upload-files-tab')?.click()}
                          disabled={uploadingIrs}
                          className="px-3 py-2 text-xs font-semibold rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50"
                        >
                          {uploadingIrs ? 'Uploading...' : fileUrls.irsUrl ? 'Replace' : 'Upload'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {/* Task Assignment Modal */}
      {showTaskModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowTaskModal(false)}
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Assign New Task</h2>
              <button
                onClick={() => setShowTaskModal(false)}
                className="text-slate-400 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Task Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-2xl focus:ring-lumin-primary focus:border-lumin-primary"
                  placeholder="Enter task title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-2xl focus:ring-lumin-primary focus:border-lumin-primary"
                  placeholder="Enter task description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as PriorityLevel })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-2xl focus:ring-lumin-primary focus:border-lumin-primary"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Due Date (Optional)</label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-2xl focus:ring-lumin-primary focus:border-lumin-primary"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateTask}
                className="flex-1 px-4 py-2 bg-lumin-primary text-white font-semibold rounded-2xl hover:bg-[#014f70]"
              >
                Create Task
              </button>
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  setNewTask({ title: '', description: '', priority: 'medium', due_date: '' });
                }}
                className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 font-semibold rounded-2xl hover:bg-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step Assignment Modal */}
      {showStepModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowStepModal(false);
            setSelectedStepId('');
          }}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Assign Onboarding Step</h2>
              <button
                onClick={() => {
                  setShowStepModal(false);
                  setSelectedStepId('');
                }}
                className="text-slate-400 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {availableSteps.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-600">All available steps are already assigned to this loan officer.</p>
                <button
                  onClick={() => {
                    setShowStepModal(false);
                    setSelectedStepId('');
                  }}
                  className="mt-4 px-4 py-2 bg-slate-200 text-slate-700 font-semibold rounded-2xl hover:bg-slate-300"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-4">
                  Select a step to assign to {loanOfficer?.first_name} {loanOfficer?.last_name}
                </p>

                <div className="space-y-2 mb-6">
                  {availableSteps.map((step) => (
                    <div
                      key={step.id}
                      onClick={() => setSelectedStepId(step.id)}
                      className={`p-4 border-2 rounded-2xl cursor-pointer transition-colors ${
                        selectedStepId === step.id
                          ? 'border-lumin-primary bg-[#e0f2ff]'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-slate-500">#{step.sequential_order}</span>
                            <h3 className="font-semibold text-slate-900">{step.task_name}</h3>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-xl ${priorityColors[step.priority]}`}>
                              {step.priority.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{activityTypeCopy[step.activity_type].label}</p>
                          {step.notes && <p className="text-sm text-slate-500 mt-2">{replaceTemplateVars(step.notes, loanOfficer?.location_id)}</p>}
                        </div>
                        {selectedStepId === step.id && <CheckCircleSolid className="h-6 w-6 text-lumin-primary ml-2" />}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleAssignStep}
                    disabled={!selectedStepId}
                    className={`flex-1 px-4 py-2 font-semibold rounded-2xl ${
                      selectedStepId
                        ? 'bg-lumin-primary text-white hover:bg-[#014f70]'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Assign Step
                  </button>
                  <button
                    onClick={() => {
                      setShowStepModal(false);
                      setSelectedStepId('');
                    }}
                    className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 font-semibold rounded-2xl hover:bg-slate-300"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* A2P SOP Modal */}
      {showA2PSOPModal && a2pBusinessRegistration && loanOfficer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="relative bg-white rounded-3xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center rounded-t-3xl">
              <h3 className="text-xl font-semibold text-slate-900">A2P SMS Standard Operating Procedure</h3>
              <button onClick={() => setShowA2PSOPModal(false)} className="text-slate-400 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4">
              {/* Business Information */}
              <div className="mb-6 p-4 bg-slate-50 rounded-2xl">
                <h4 className="font-semibold text-slate-900 mb-3">Business Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-semibold text-slate-700">Business Name:</span>
                    <p className="text-slate-900">{a2pBusinessRegistration.business_name}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-700">Loan Officer:</span>
                    <p className="text-slate-900">
                      {loanOfficer.first_name} {loanOfficer.last_name}
                    </p>
                  </div>
                  {a2pBusinessRegistration.street_address && (
                    <div>
                      <span className="font-semibold text-slate-700">Address:</span>
                      <p className="text-slate-900">{a2pBusinessRegistration.street_address}</p>
                    </div>
                  )}
                  {a2pBusinessRegistration.city && a2pBusinessRegistration.state && (
                    <div>
                      <span className="font-semibold text-slate-700">City, State:</span>
                      <p className="text-slate-900">
                        {a2pBusinessRegistration.city}, {a2pBusinessRegistration.state}{' '}
                        {a2pBusinessRegistration.zip_code}
                      </p>
                    </div>
                  )}
                  {a2pBusinessRegistration.ein && (
                    <div>
                      <span className="font-semibold text-slate-700">EIN:</span>
                      <p className="text-slate-900">{a2pBusinessRegistration.ein}</p>
                    </div>
                  )}
                </div>
                {a2pBusinessRegistration.irs_paperwork && (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        const url = fileUrls.irsUrl || a2pBusinessRegistration.irs_paperwork;
                        if (url) window.open(url, '_blank');
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-2xl hover:bg-blue-700"
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                      View IRS Paperwork
                    </button>
                  </div>
                )}
              </div>

              {/* Use Case Description */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-slate-900">Use Case Description</h4>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        'We will be using SMS for appointment confirmations, appointment reminders, communicating with customers, and informational updates to customers who have opted in. They have the option to opt into confirmation & reminders and/or marketing communications.',
                        'useCase'
                      )
                    }
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-xl"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    {copiedSection === 'useCase' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  We will be using SMS for appointment confirmations, appointment reminders, communicating with customers,
                  and informational updates to customers who have opted in. They have the option to opt into confirmation &
                  reminders and/or marketing communications.
                </p>
              </div>

              {/* Sample Messages */}
              <div className="mb-6">
                <h4 className="font-semibold text-slate-900 mb-3">Sample Messages</h4>

                <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-2xl relative">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-sm font-semibold text-blue-900">Sample Message #1</p>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          `Hello, this is ${loanOfficer.first_name} from ${a2pBusinessRegistration.business_name}. I am following up with you about our meeting yesterday, would you have time to discuss this today? Reply STOP to cancel.`,
                          'message1'
                        )
                      }
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-white hover:bg-slate-50 rounded-xl border border-blue-200"
                    >
                      <DocumentDuplicateIcon className="h-3 w-3" />
                      {copiedSection === 'message1' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-slate-800">
                    Hello, this is {loanOfficer.first_name} from {a2pBusinessRegistration.business_name}. I am following up
                    with you about our meeting yesterday, would you have time to discuss this today? Reply STOP to cancel.
                  </p>
                </div>

                <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-2xl relative">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-sm font-semibold text-blue-900">Sample Message #2</p>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          `Hey Brian! This is ${loanOfficer.first_name} from ${a2pBusinessRegistration.business_name}. I see that you weren't able to make it for our appointment. Would you like to reschedule? Reply STOP to unsubscribe.`,
                          'message2'
                        )
                      }
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-white hover:bg-slate-50 rounded-xl border border-blue-200"
                    >
                      <DocumentDuplicateIcon className="h-3 w-3" />
                      {copiedSection === 'message2' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-slate-800">
                    Hey Brian! This is {loanOfficer.first_name} from {a2pBusinessRegistration.business_name}. I see that you
                    weren't able to make it for our appointment. Would you like to reschedule? Reply STOP to unsubscribe.
                  </p>
                </div>
              </div>

              {/* Check All 4 Boxes */}
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                <p className="text-sm font-semibold text-emerald-900">Check All 4 Boxes</p>
              </div>

              {/* How do lead/contacts consent to receive messages */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-slate-900">How do lead/contacts consent to receive messages?</h4>
                  <button
                    onClick={() => {
                      const domain = loanOfficer.website_url || '[A2P domain not set]';
                      copyToClipboard(
                        `End users opt-in by visiting - ${domain}/contact-us, click on Contact button, and filling in their details within the survey. Users can check a box to receive notifications from us when opting in. They have the option to check a box to get promotional messages from us if they provide consent.`,
                        'consent'
                      );
                    }}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-xl"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    {copiedSection === 'consent' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  End users opt-in by visiting -{' '}
                  <span className="font-semibold text-blue-600">{loanOfficer.website_url || '[A2P domain not set]'}/contact-us</span>, click on Contact button, and filling in their details within the survey. Users can check a box to
                  receive notifications from us when opting in. They have the option to check a box to get promotional messages
                  from us if they provide consent.
                </p>
              </div>

              {/* Opt-In Message */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-slate-900">Opt-In Message</h4>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `You have successfully opted-in to ${a2pBusinessRegistration.business_name} messaging service. Reply STOP to unsubscribe.`,
                        'optIn'
                      )
                    }
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-xl"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    {copiedSection === 'optIn' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-slate-800 italic">
                  You have successfully opted-in to {a2pBusinessRegistration.business_name} messaging service. Reply STOP to
                  unsubscribe.
                </p>
              </div>

              {/* Note */}
              <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-2xl">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Use the copy buttons to copy each section for A2P registration submission.{' '}
                  {!loanOfficer.website_url &&
                    "The A2P domain is not set in the loan officer's profile - please update the website URL field before submitting."}
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-end rounded-b-3xl">
              <button
                onClick={() => setShowA2PSOPModal(false)}
                className="px-6 py-2 bg-slate-600 text-white rounded-2xl hover:bg-slate-700 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Notes Tab */}
        {activeTab === 'notes' && id && (
          <LOComments loanOfficerId={id} />
        )}

      {/* Step Detail Modal */}
      {selectedStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedStep(null)}>
          <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold text-slate-900">{selectedStep.step.task_name}</h2>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className={`px-3 py-1 text-xs rounded-full ${activityAccent[selectedStep.step.activity_type]}`}>
                      {activityTypeCopy[selectedStep.step.activity_type].label}
                    </span>
                    <span className={`px-3 py-1 text-xs rounded-full ${priorityColors[selectedStep.step.priority]}`}>
                      {selectedStep.step.priority.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 text-xs rounded-full ${statusColors[selectedStep.status]}`}>
                      {statusLabels[selectedStep.status]}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedStep(null)}
                  className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedStep.step.notes ? (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Instructions</h3>
                  <div className="space-y-4">
                    {replaceTemplateVars(selectedStep.step.notes, loanOfficer?.location_id).split('\n').map((line, idx) => {
                      // Check if line contains markdown link [text](url)
                      const markdownLinkMatch = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
                      if (markdownLinkMatch) {
                        const [, text, url] = markdownLinkMatch;
                        const beforeLink = line.substring(0, line.indexOf('['));
                        return (
                          <div key={idx} className="flex flex-col gap-2">
                            {beforeLink && <p className="text-slate-700">{beforeLink.trim()}</p>}
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-4 py-2 rounded-2xl bg-lumin-primary text-white font-semibold text-sm hover:bg-[#014c68] transition-colors w-fit"
                            >
                              {text}
                            </a>
                          </div>
                        );
                      }
                      // Check if line contains plain URL
                      const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
                      if (urlMatch) {
                        const parts = line.split(/(https?:\/\/[^\s]+)/);
                        return (
                          <p key={idx} className="text-slate-700">
                            {parts.map((part, i) => {
                              if (part.match(/^https?:\/\//)) {
                                return (
                                  <a
                                    key={i}
                                    href={part}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-lumin-primary hover:text-[#014c68] underline"
                                  >
                                    {part}
                                  </a>
                                );
                              }
                              return part;
                            })}
                          </p>
                        );
                      }
                      // Regular text line
                      return line.trim() ? (
                        <p key={idx} className="text-slate-700">{line}</p>
                      ) : (
                        <div key={idx} className="h-2" />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No additional instructions provided.</p>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3 justify-end">
              {selectedStep.status === 'not_started' && (
                <button
                  onClick={() => handleStepStatusChange(selectedStep.id, 'in_progress', true)}
                  disabled={updatingStepId === selectedStep.id}
                  className="px-6 py-3 rounded-2xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68]"
                >
                  Start This Step
                </button>
              )}
              {selectedStep.status === 'in_progress' && (
                <button
                  onClick={() => {
                    handleMarkComplete(selectedStep.id);
                    setSelectedStep(null);
                  }}
                  disabled={updatingStepId === selectedStep.id}
                  className="px-6 py-3 rounded-2xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Mark as Complete
                </button>
              )}
              {selectedStep.status === 'completed' && (
                <button
                  onClick={() => {
                    handleStepStatusChange(selectedStep.id, 'not_started');
                    setSelectedStep(null);
                  }}
                  disabled={updatingStepId === selectedStep.id}
                  className="px-6 py-3 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  Reset Step
                </button>
              )}
              {selectedStep.status === 'blocked' && (
                <button
                  onClick={() => {
                    handleStepStatusChange(selectedStep.id, 'in_progress');
                    setSelectedStep(null);
                  }}
                  disabled={updatingStepId === selectedStep.id}
                  className="px-6 py-3 rounded-2xl text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700"
                >
                  Unblock Step
                </button>
              )}
              <button
                onClick={() => setSelectedStep(null)}
                className="px-6 py-3 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminShell>
  );
};

export default LoanOfficerDetail;
